import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import './PricingCatalogPanel.css';

const PALETTE = ['#27ae60', '#2980b9', '#8e44ad', '#e67e22', '#c0392b', '#16a085'];

function formatVal(val, type) {
    if (val === null || val === undefined || val === false || val === 0) return null;
    if (type === 'bool') return '✓';
    if (type === 'int') return val === -1 ? '∞' : String(val);
    if (type === 'price') return val === 0 ? 'Бесплатно' : `${Number(val).toLocaleString('ru')} ₽`;
    return String(val);
}

export default function PricingCatalogPanel({ shopPlans = [], onChangePlan, planChanging, refreshKey }) {
    const { request } = useApi();
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [compareOpen, setCompareOpen] = useState(false);
    const [showDowngradeInfo, setShowDowngradeInfo] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await request('/api/v1/billing/tool-catalog');
            setCatalog(res);
        } catch {
            setError('Не удалось загрузить каталог');
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => { load(); }, [load, refreshKey]);

    if (loading) return <div className="pcp__loading">Загрузка…</div>;
    if (error) return <div className="pcp__error">{error}</div>;
    if (!catalog) return null;

    const { categories, plans } = catalog;

    // Итого: сумма ежедневных ставок по тарифам магазинов
    const totalDaily = shopPlans.reduce((sum, s) => {
        const plan = plans.find(p => p.key === s.plan_type);
        return sum + (plan ? plan.price_daily : 0);
    }, 0);
    const totalMonthly = shopPlans.reduce((sum, s) => {
        const plan = plans.find(p => p.key === s.plan_type);
        return sum + (plan ? plan.price_monthly : 0);
    }, 0);

    const getPlanDowngradeBlockInfo = (shop) => {
        const currentPlanIdx = plans.findIndex(p => p.key === shop.plan_type);
        const PLAN_ORDER = plans.map(p => p.key);
        const isCurrentDowngrade = (targetPlanKey) => {
            const targetIdx = PLAN_ORDER.indexOf(targetPlanKey);
            return targetIdx < currentPlanIdx && currentPlanIdx >= 0;
        };

        if (!shop.plan_activated_at) return null;
        const activatedDate = new Date(shop.plan_activated_at);
        const now = new Date();
        const daysPassed = Math.floor((now - activatedDate) / (1000 * 60 * 60 * 24));
        const MIN_DAYS = 7;
        const daysLeft = Math.max(0, MIN_DAYS - daysPassed);

        return { isDowngradeAvailable: daysLeft === 0, daysLeft, daysPassed, isCurrentDowngrade };
    };

    const downgradeBlockedShops = shopPlans.filter(shop => {
        const blockInfo = getPlanDowngradeBlockInfo(shop);
        return blockInfo && !blockInfo.isDowngradeAvailable;
    });

    return (
        <div className="pcp">
            {showDowngradeInfo && downgradeBlockedShops.length > 0 && (
                <div style={{
                    padding: '12px 16px',
                    marginBottom: '16px',
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '6px',
                    color: '#856404',
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                }}>
                    <div>
                        <strong>⚠ Минимальный срок при понижении тарифа:</strong>
                        <br />
                        Тариф можно понизить не ранее чем через 7 дней с момента активации.
                        {downgradeBlockedShops.map(shop => {
                            const blockInfo = getPlanDowngradeBlockInfo(shop);
                            return <div key={shop.account_id} style={{ marginTop: '4px' }}>
                                • <strong>{shop.account_name}</strong> — через {blockInfo.daysLeft} дн.
                            </div>;
                        })}
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowDowngradeInfo(false)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: '#856404',
                            padding: '0',
                            marginLeft: '12px',
                            flexShrink: 0
                        }}
                        title="Закрыть"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* ── Shop plan assignment table ── */}
            <div className="pcp__shops">
                {shopPlans.length === 0 ? (
                    <p className="pcp__no-shops">Магазины не подключены или загружаются…</p>
                ) : (
                    <table className="pcp__shop-table">
                        <thead>
                            <tr>
                                <th>Магазин</th>
                                <th>Текущий тариф</th>
                                <th>Сумма / мес</th>
                                <th>Изменить</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shopPlans.map((shop, shopIdx) => {
                                const currentPlan = plans.find(p => p.key === shop.plan_type);
                                const colorIdx = plans.findIndex(p => p.key === shop.plan_type);
                                const blockInfo = getPlanDowngradeBlockInfo(shop);
                                const marketplaceLabel = shop.marketplace_type === 'wildberries' ? 'WB' : 'OZON';
                                const marketplaceBg = shop.marketplace_type === 'wildberries' ? '#fff3e0' : '#e8f4f8';
                                const marketplaceFg = shop.marketplace_type === 'wildberries' ? '#e65100' : '#0277bd';
                                return (
                                    <tr key={shop.account_id}>
                                        <td className="pcp__shop-name">
                                            {shop.account_name}
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    marginLeft: '8px',
                                                    padding: '4px 8px',
                                                    background: marketplaceBg,
                                                    color: marketplaceFg,
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}
                                            >
                                                {marketplaceLabel}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className="pcp__plan-badge"
                                                style={{ '--pc': PALETTE[colorIdx >= 0 ? colorIdx : 0] }}
                                            >
                                                {currentPlan ? currentPlan.label : shop.plan_type}
                                            </span>
                                        </td>
                                        <td className="pcp__shop-price">
                                            {currentPlan
                                                ? currentPlan.price_monthly === 0
                                                    ? 'Бесплатно'
                                                    : `${currentPlan.price_monthly.toLocaleString('ru')} ₽`
                                                : '—'
                                            }
                                        </td>
                                        <td className="pcp__shop-actions">
                                            {plans.map((plan, idx) => {
                                                if (plan.key === shop.plan_type) return null;
                                                const isDowngrade = blockInfo && blockInfo.isCurrentDowngrade(plan.key);
                                                const isBlocked = isDowngrade && !blockInfo.isDowngradeAvailable;
                                                const title = isBlocked
                                                    ? `Нажмите чтобы узнать когда можно понизить`
                                                    : `Переключить на «${plan.label}»`;
                                                return (
                                                    <button
                                                        key={plan.key}
                                                        type="button"
                                                        className="pcp__switch-btn"
                                                        style={{ '--pc': PALETTE[idx % PALETTE.length] }}
                                                        disabled={planChanging === shop.account_id}
                                                        onClick={() => {
                                                            if (isBlocked) {
                                                                setShowDowngradeInfo(true);
                                                            } else {
                                                                onChangePlan && onChangePlan(shop.account_id, plan.key);
                                                            }
                                                        }}
                                                        title={title}
                                                    >
                                                        {planChanging === shop.account_id ? '…' : isBlocked ? '🔒' : plan.label}
                                                    </button>
                                                );
                                            })}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {shopPlans.length > 1 && (
                            <tfoot>
                                <tr className="pcp__total-row">
                                    <td colSpan={2}><strong>Итого</strong></td>
                                    <td>
                                        <strong>
                                            {totalMonthly === 0 ? 'Бесплатно' : `${totalMonthly.toLocaleString('ru')} ₽/мес`}
                                        </strong>
                                        {totalDaily > 0 && (
                                            <span className="pcp__total-daily">
                                                {' '}≈ {totalDaily.toLocaleString('ru', { maximumFractionDigits: 2 })} ₽/день
                                            </span>
                                        )}
                                    </td>
                                    <td />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                )}
            </div>

            {/* ── Plan pricing reference ── */}
            <div className="pcp__plan-ref">
                {plans.map((plan, idx) => (
                    <div key={plan.key} className="pcp__plan-ref-item" style={{ '--pc': PALETTE[idx % PALETTE.length] }}>
                        <span className="pcp__plan-ref-name">{plan.label}</span>
                        <span className="pcp__plan-ref-price">
                            {plan.price_monthly === 0
                                ? 'Бесплатно'
                                : `${plan.price_monthly.toLocaleString('ru')} ₽/мес`
                            }
                        </span>
                        {plan.price_daily > 0 && (
                            <span className="pcp__plan-ref-daily">
                                {plan.price_daily.toLocaleString('ru', { maximumFractionDigits: 2 })} ₽/день
                            </span>
                        )}
                    </div>
                ))}
            </div>

            {/* ── Feature comparison (collapsible) ── */}
            <div className="pcp__compare-wrap">
                <button
                    type="button"
                    className="pcp__compare-toggle"
                    onClick={() => setCompareOpen(o => !o)}
                >
                    {compareOpen ? '▲' : '▼'} Сравнение возможностей по тарифам
                </button>
                {compareOpen && (
                    <div className="pcp__compare">
                        <table className="pcp__table">
                            <thead>
                                <tr>
                                    <th className="pcp__th-feature">Возможности</th>
                                    {plans.map((p, idx) => (
                                        <th key={p.key} className="pcp__th-plan" style={{ '--pc': PALETTE[idx % PALETTE.length] }}>
                                            {p.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map(cat => (
                                    <React.Fragment key={cat.key}>
                                        <tr className="pcp__cat-row">
                                            <td colSpan={plans.length + 1} className="pcp__cat-label">{cat.label}</td>
                                        </tr>
                                        {cat.tools.map(tool => {
                                            const cells = plans.map(p => formatVal(tool.plan_values[p.key], tool.type));
                                            if (cells.every(c => !c)) return null;
                                            return (
                                                <tr key={tool.key} className="pcp__tool-row">
                                                    <td className="pcp__tool-name">
                                                        {tool.icon && <span className="pcp__tool-icon">{tool.icon}</span>}
                                                        <span>
                                                            {tool.title}
                                                            {tool.state === 'beta' && <span className="pcp__beta-badge">beta</span>}
                                                        </span>
                                                    </td>
                                                    {plans.map((p, i) => (
                                                        <td key={p.key} className={`pcp__val ${cells[i] ? 'pcp__val--yes' : 'pcp__val--no'}`}>
                                                            {cells[i] || '—'}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
